import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Progress } from '../ui/progress';
import { 
  Maximize2, Minimize2, Cloud, CloudOff, 
  X, ChevronDown, ChevronUp, RotateCcw, ExternalLink, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import FeelingsNeedsHelper from '../FeelingsNeedsHelper';
import EbookPageFooter from '../shared/EbookPageFooter';
import FloatingFeelingsHelper from '../shared/FloatingFeelingsHelper';
import ChapterSummaryGenerator from '../shared/ChapterSummaryGenerator';
import ChoiceNeuroplasticityContent from '../shared/ChoiceNeuroplasticityContent';
import {
  FeelIcon, ObserveIcon, NeedIcon, GoldIcon,
  AlarmIcon, AngerIcon, SadnessIcon, PeaceIcon, GuiltShameIcon,
  ExerciseIcon, HomeworkIcon, PartnerIcon,
  VideoIcon, ReadingIcon, SaveIcon, ChapterIcon,
  ArrowRightIcon, ArrowLeftIcon, CheckIcon, InsightIcon, ThoughtIcon
} from '../icons/ElchmeIcons';

const CHAPTER_IMAGES = {
  hero: 'https://images.unsplash.com/photo-1547098817-e37bf4d7a8ad?w=1200&q=80',
  feelings: 'https://images.unsplash.com/photo-1499209974431-9dddcece7f88?w=800&q=80',
  emotions: 'https://images.unsplash.com/photo-1517960413843-0aee8e2b3285?w=800&q=80',
  connection: 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=800&q=80',
  reflection: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80',
};

// Exercise 1 Data - Page 99
const exercise1Data = [
  { id: 1, text: "NVC is difficult." },
  { id: 2, text: "How could you speak to me like that?" },
  { id: 3, text: "These people are so shallow." },
  { id: 4, text: "I see that you did the dishes without me asking you." },
  { id: 5, text: "You are so clumsy! You could have been hurt." },
  { id: 6, text: "He told me that he didn't want to be with me." },
  { id: 7, text: "She keeps telling me what to do. Why doesn't she leave me alone?" },
  { id: 8, text: "I know I'm going to get this promotion." },
  { id: 9, text: "I don't really want to talk about that right now." },
  { id: 10, text: "How do I look?" },
  { id: 11, text: "I'm not sure what you want me to do." },
  { id: 12, text: "Could you possibly do that any slower?" },
  { id: 13, text: "You should have come into work on time today." },
  { id: 14, text: "She is so much better looking than I am." },
];

// Exercise 1 Answers - Page 104
const exercise1Answers = {
  1: "Afraid, uncomfortable, embarrassed, confused",
  2: "Angry, frustrated, hurt, vulnerable, discouraged",
  3: "Restless, uneasy, tired, fidgety, nervous",
  4: "Grateful, surprised, confused, excited",
  5: "Afraid, concerned, panicked, scared",
  6: "Sad, hopeless, discouraged, dejected",
  7: "Frustrated, annoyed, agitated",
  8: "Hopeful, optimistic, nervous",
  9: "Aloof, disconnected, indifferent, numb",
  10: "Self-conscious, nervous, apprehensive",
  11: "Nervous, anxious, unsettled",
  12: "Frustrated, impatient, irritated",
  13: "Annoyed, disappointed",
  14: "Envious, jealous, sad, disappointed",
};

// Exercise 2 Data - Page 102
const exercise2Data = [
  { id: 1, text: "I feel that you are so angry at me.", isFeeling: false, translation: "When I see you behave that way, I am afraid." },
  { id: 2, text: "I'm feeling that we are not getting along any more.", isFeeling: false, translation: "I am sad because I am not receiving the quality of connection I would like in our relationship." },
  { id: 3, text: "I'm afraid to say what I would like to say.", isFeeling: true, translation: null },
  { id: 4, text: "I felt betrayed when you said that to me.", isFeeling: false, translation: "When you said that to me, I felt frustrated because..." },
  { id: 5, text: "When you criticize me, I feel put down.", isFeeling: false, translation: "When we spoke earlier this morning, I felt ashamed." },
  { id: 6, text: "I feel that you are controlling me.", isFeeling: false, translation: "When you told me that I wasn't allowed to leave my room, I felt irate." },
  { id: 7, text: "When you said you wouldn't do that for me, I felt hurt.", isFeeling: true, translation: null },
  { id: 8, text: "I feel like I am one with the universe.", isFeeling: false, translation: "I feel peaceful and connected." },
];

// Non-Feeling Words List - Page 103
const nonFeelingWords = [
  "abandoned", "abused", "attacked", "belittled", "betrayed", "blamed",
  "bullied", "cheated", "coerced", "cornered", "criticized", "diminished",
  "disrespected", "dumped", "harassed", "ignored", "insulted", "interrupted",
  "intimidated", "invalidated", "invisible", "isolated", "judged", "lectured to",
  "left out", "let down", "manipulated", "misunderstood", "neglected", "not heard",
  "overworked", "patronized", "pressured", "provoked", "put down", "rejected",
  "taken for granted", "threatened", "unappreciated", "unheard", "unloved", "unseen",
  "unsupported", "unwanted", "used", "victimized", "violated", "worthless"
];

// Feelings Lists - Comprehensive
const feelingsSatisfied = [
  { category: 'AFFECTIONATE', feelings: ['compassionate', 'friendly', 'loving', 'tender', 'warm'] },
  { category: 'CONFIDENT', feelings: ['empowered', 'open', 'proud', 'safe', 'secure'] },
  { category: 'ENGAGED', feelings: ['absorbed', 'alert', 'curious', 'fascinated', 'interested'] },
  { category: 'EXCITED', feelings: ['amazed', 'eager', 'energetic', 'enthusiastic', 'lively'] },
  { category: 'GRATEFUL', feelings: ['appreciative', 'moved', 'thankful', 'touched'] },
  { category: 'HOPEFUL', feelings: ['expectant', 'encouraged', 'optimistic'] },
  { category: 'JOYFUL', feelings: ['amused', 'delighted', 'glad', 'happy', 'pleased'] },
  { category: 'PEACEFUL', feelings: ['calm', 'centered', 'content', 'relaxed', 'serene'] },
  { category: 'REFRESHED', feelings: ['enlivened', 'rejuvenated', 'renewed', 'rested', 'restored'] },
];

const feelingsUnsatisfied = [
  { category: 'AFRAID', feelings: ['anxious', 'panicked', 'scared', 'terrified', 'worried'] },
  { category: 'ANNOYED', feelings: ['aggravated', 'frustrated', 'impatient', 'irritated'] },
  { category: 'ANGRY', feelings: ['furious', 'outraged', 'resentful'] },
  { category: 'CONFUSED', feelings: ['ambivalent', 'hesitant', 'lost', 'perplexed', 'torn'] },
  { category: 'DISCONNECTED', feelings: ['alienated', 'bored', 'distant', 'numb', 'withdrawn'] },
  { category: 'EMBARRASSED', feelings: ['ashamed', 'guilty', 'self-conscious'] },
  { category: 'FATIGUE', feelings: ['exhausted', 'tired', 'weary', 'worn out'] },
  { category: 'SAD', feelings: ['depressed', 'disappointed', 'discouraged', 'hopeless', 'lonely'] },
  { category: 'PAIN', feelings: ['grief', 'heartbroken', 'hurt', 'miserable', 'regretful'] },
];

export default function Chapter3Presentation() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialSection = parseInt(searchParams.get('section')) || 0;
  const [currentSection, setCurrentSection] = useState(initialSection);
  const [isPresentationMode, setIsPresentationMode] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [isHelperOpen, setIsHelperOpen] = useState(false);
  const [expandedItem, setExpandedItem] = useState('ex1-1'); // Auto-expand first question
  const [showAnswers, setShowAnswers] = useState({});

  // Exercise responses
  const [responses, setResponses] = useState(() => {
    const saved = localStorage.getItem('chapter3Responses');
    return saved ? JSON.parse(saved) : {};
  });

  // Exercise 1 user guesses
  const [exercise1Guesses, setExercise1Guesses] = useState(() => {
    const saved = localStorage.getItem('chapter3Exercise1');
    return saved ? JSON.parse(saved) : {};
  });

  // Exercise 2 selections
  const [exercise2Selections, setExercise2Selections] = useState({});
  const [exercise2Notes, setExercise2Notes] = useState(() => {
    const saved = localStorage.getItem('chapter3Exercise2Notes');
    return saved ? JSON.parse(saved) : {};
  });
  const [showExercise2Answers, setShowExercise2Answers] = useState(false);
  const [showSummaryGenerator, setShowSummaryGenerator] = useState(false);

  // Exercise 3 responses
  const [exercise3Responses, setExercise3Responses] = useState(() => {
    const saved = localStorage.getItem('chapter3Exercise3');
    return saved ? JSON.parse(saved) : { 1: { when: '', felt: '' }, 2: { when: '', felt: '' }, 3: { when: '', felt: '' }, 4: { when: '', felt: '' }, 5: { when: '', felt: '' } };
  });

  // Exercise 4 responses
  const [exercise4Responses, setExercise4Responses] = useState(() => {
    const saved = localStorage.getItem('chapter3Exercise4');
    return saved ? JSON.parse(saved) : { 1: { word: '', scenario: '', translation: '' }, 2: { word: '', scenario: '', translation: '' }, 3: { word: '', scenario: '', translation: '' }, 4: { word: '', scenario: '', translation: '' } };
  });

  // Sections - with PDF page references
  const sections = [
    { id: 'intro', title: 'Chapter 3: Feelings', page: 99 },
    { id: 'foundation-page11', title: 'Foundation: The OFNR Model', page: 11 },
    { id: 'choice-page51b', title: 'The Power of Choice', page: '51B' },
    { id: 'exercise1-intro', title: 'Exercise 1: Guess the Feelings', page: 99 },
    { id: 'exercise1-practice', title: 'Exercise 1: Practice', page: 99 },
    { id: 'feelings-satisfied', title: 'Feelings When Needs Are Met', page: 100 },
    { id: 'feelings-unsatisfied', title: 'Feelings When Needs Are Not Met', page: 101 },
    { id: 'exercise2-intro', title: 'Exercise 2: Feeling or Thought?', page: 102 },
    { id: 'exercise2-practice', title: 'Exercise 2: Practice', page: 102 },
    { id: 'exercise3', title: 'Exercise 3: Observations & Feelings', page: 102 },
    { id: 'non-feeling-words', title: 'Non-Feeling Words', page: 103 },
    { id: 'exercise4', title: 'Exercise 4: Translate Non-Feeling Words', page: 103 },
    { id: 'exercise1-answers', title: 'Possible Answers: Exercise 1', page: 104 },
    { id: 'exercise2-answers', title: 'Possible Answers: Exercise 2', page: 104 },
    { id: 'alarm-feelings', title: 'Alarm Feelings: Gateway Emotions', page: 105 },
    { id: 'acceptance-mourning', title: 'Acceptance, Clarity, and Mourning', page: 106 },
    { id: 'homework', title: 'Chapter 3 Homework', page: 107 },
    { id: 'chapter-summary', title: 'Create Your Summary', page: null },
  ];

  const progress_pct = ((currentSection + 1) / sections.length) * 100;

  // Check login status
  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);
  }, []);

  // Fetch progress from server
  const fetchUserProgress = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/workbook-progress/chapter3`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.responses) {
          setResponses(data.responses);
          localStorage.setItem('chapter3Responses', JSON.stringify(data.responses));
        }
        if (data.exercise1Guesses) {
          setExercise1Guesses(data.exercise1Guesses);
          localStorage.setItem('chapter3Exercise1', JSON.stringify(data.exercise1Guesses));
        }
        if (data.exercise3Responses) {
          setExercise3Responses(data.exercise3Responses);
          localStorage.setItem('chapter3Exercise3', JSON.stringify(data.exercise3Responses));
        }
        if (data.exercise4Responses) {
          setExercise4Responses(data.exercise4Responses);
          localStorage.setItem('chapter3Exercise4', JSON.stringify(data.exercise4Responses));
        }
        setLastSyncTime(new Date());
      }
    } catch (error) {
      console.log('Could not fetch progress from server');
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      fetchUserProgress();
    }
  }, [isLoggedIn, fetchUserProgress]);

  // Save progress
  const saveProgress = async () => {
    localStorage.setItem('chapter3Responses', JSON.stringify(responses));
    localStorage.setItem('chapter3Exercise1', JSON.stringify(exercise1Guesses));
    localStorage.setItem('chapter3Exercise2Notes', JSON.stringify(exercise2Notes));
    localStorage.setItem('chapter3Exercise3', JSON.stringify(exercise3Responses));
    localStorage.setItem('chapter3Exercise4', JSON.stringify(exercise4Responses));
    
    if (isLoggedIn) {
      setIsSyncing(true);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/workbook-progress/chapter3`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ 
            responses, 
            exercise1Guesses, 
            exercise2Notes,
            exercise3Responses, 
            exercise4Responses 
          })
        });
        if (response.ok) {
          setLastSyncTime(new Date());
          toast.success('Progress saved to your account!');
        }
      } catch (error) {
        toast.error('Saved locally (server sync failed)');
      } finally {
        setIsSyncing(false);
      }
    } else {
      toast.success('Saved locally! Sign in to sync across devices.');
    }
  };

  const resetAll = () => {
    if (window.confirm('Reset all Chapter 3 responses? This cannot be undone.')) {
      setResponses({});
      setExercise1Guesses({});
      setExercise3Responses({ 1: { when: '', felt: '' }, 2: { when: '', felt: '' }, 3: { when: '', felt: '' }, 4: { when: '', felt: '' }, 5: { when: '', felt: '' } });
      setExercise4Responses({ 1: { word: '', scenario: '', translation: '' }, 2: { word: '', scenario: '', translation: '' }, 3: { word: '', scenario: '', translation: '' }, 4: { word: '', scenario: '', translation: '' } });
      localStorage.removeItem('chapter3Responses');
      localStorage.removeItem('chapter3Exercise1');
      localStorage.removeItem('chapter3Exercise3');
      localStorage.removeItem('chapter3Exercise4');
      toast.success('All responses reset!');
    }
  };

  // Toggle presentation mode
  const togglePresentationMode = async () => {
    if (!isPresentationMode) {
      try {
        if (document.documentElement.requestFullscreen) {
          await document.documentElement.requestFullscreen();
        }
      } catch (e) { console.log('Fullscreen not available'); }
    } else {
      try {
        if (document.fullscreenElement) {
          await document.exitFullscreen();
        }
      } catch (e) { console.log('Exit fullscreen failed'); }
    }
    setIsPresentationMode(!isPresentationMode);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isPresentationMode) {
        if (e.key === 'ArrowRight' || e.key === ' ') {
          e.preventDefault();
          setCurrentSection(prev => Math.min(prev + 1, sections.length - 1));
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          setCurrentSection(prev => Math.max(prev - 1, 0));
        } else if (e.key === 'Escape') {
          setIsPresentationMode(false);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isPresentationMode, sections.length]);

  // Render content based on section
  const renderContent = () => {
    switch (currentSection) {
      // INTRO
      case 0:
        return (
          <div className="space-y-8 animate-fadeIn" data-testid="chapter3-intro">
            <div className="relative rounded-xl overflow-hidden shadow-2xl h-64 lg:h-96">
              <img src={CHAPTER_IMAGES.hero} alt="Feelings" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 to-transparent flex items-end p-8">
                <div>
                  <h2 className="text-4xl lg:text-5xl font-bold text-white mb-2">Chapter 3: Feelings</h2>
                  <p className="text-xl text-rose-200">Pages 99-107 • Expanding Emotional Vocabulary</p>
                </div>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-rose-900/20 border border-rose-700/30 rounded-xl p-6">
                <FeelIcon size={32} className="mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">Genuine Feelings</h3>
                <p className="text-slate-300">
                  Learn to distinguish <span className="text-rose-300 font-semibold">actual feelings</span> from 
                  thoughts disguised as feelings. Feelings connect us to our needs.
                </p>
              </div>
              <div className="bg-amber-900/20 border border-amber-700/30 rounded-xl p-6">
                <AlarmIcon size={32} className="mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">Alarm Feelings</h3>
                <p className="text-slate-300">
                  Understand <span className="text-amber-300 font-semibold">anger, guilt, shame & depression</span> as 
                  gateway feelings pointing to deeper needs.
                </p>
              </div>
            </div>
            
            {/* Foundation Links */}
            <div className="bg-gradient-to-r from-purple-900/30 to-indigo-900/30 border border-purple-500/30 rounded-xl p-6">
              <h3 className="text-purple-300 font-bold mb-4 flex items-center gap-2">
                <ChapterIcon size={20} color="#A855F7" />
                Foundational Concepts
              </h3>
              <p className="text-slate-300 mb-4">Before diving into feelings, review these foundational pages:</p>
              <div className="grid md:grid-cols-2 gap-3">
                <button
                  onClick={() => setCurrentSection(1)}
                  className="flex items-center gap-3 p-3 bg-slate-800/50 hover:bg-slate-700/50 rounded-lg transition-colors text-left"
                >
                  <span className="bg-purple-600 text-white text-xs font-bold w-8 h-8 rounded-full flex items-center justify-center">p.11</span>
                  <span className="text-slate-200">The OFNR Model Foundation</span>
                  <ArrowRightIcon size={16} color="#A855F7" className="ml-auto" />
                </button>
                <button
                  onClick={() => setCurrentSection(2)}
                  className="flex items-center gap-3 p-3 bg-slate-800/50 hover:bg-slate-700/50 rounded-lg transition-colors text-left"
                >
                  <span className="bg-amber-600 text-white text-xs font-bold w-8 h-8 rounded-full flex items-center justify-center">51B</span>
                  <span className="text-slate-200">The Power of Choice</span>
                  <ArrowRightIcon size={16} color="#F59E0B" className="ml-auto" />
                </button>
              </div>
            </div>

            {/* Link to Chapter 8 */}
            <div className="bg-teal-900/20 border border-teal-500/30 rounded-xl p-6">
              <h3 className="text-teal-300 font-bold mb-3 flex items-center gap-2">
                <ExternalLink className="w-5 h-5" />
                Related: Chapter 8 Feelings List
              </h3>
              <p className="text-slate-300 mb-3">
                For a comprehensive feelings inventory, see the detailed reference on page 156.
              </p>
              <Button
                onClick={() => navigate('/workbook/chapter-8?section=0')}
                variant="outline"
                className="border-teal-600 text-teal-300 hover:bg-teal-900/30"
              >
                <span className="bg-teal-600 text-white text-xs font-bold px-2 py-1 rounded mr-2">p.156</span>
                Go to Chapter 8 Presentation
                <ArrowRightIcon size={16} className="ml-2" />
              </Button>
            </div>

            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6 text-center">
              <p className="text-slate-300 text-lg">
                In this chapter, you'll expand your emotional vocabulary and learn to identify what you're <span className="text-rose-400">truly feeling</span> beneath surface reactions.
              </p>
            </div>
          </div>
        );

      // FOUNDATION PAGE 11
      case 1:
        return (
          <div className="space-y-8 animate-fadeIn" data-testid="foundation-page11">
            <div className="text-center mb-8">
              <div className="flex items-center justify-center gap-4 mb-6">
                <ObserveIcon size={40} />
                <FeelIcon size={40} />
                <NeedIcon size={40} />
                <GoldIcon size={40} />
              </div>
              <h2 className="text-3xl font-bold text-white mb-4">The OFNR Model</h2>
              <p className="text-slate-400">Page 11 • The Foundation of NVC</p>
            </div>

            <div className="bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700 rounded-2xl p-8">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-blue-900/20 border border-blue-500/30 rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <ObserveIcon size={32} />
                    <h3 className="text-blue-300 font-bold text-xl">Observation</h3>
                  </div>
                  <p className="text-slate-300">
                    What we see, hear, or remember—without evaluation or judgment. 
                    <span className="text-blue-300"> Pure sensory data.</span>
                  </p>
                </div>
                <div className="bg-rose-900/20 border border-rose-500/30 rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <FeelIcon size={32} />
                    <h3 className="text-rose-300 font-bold text-xl">Feeling</h3>
                  </div>
                  <p className="text-slate-300">
                    Our emotional response to what we observe. 
                    <span className="text-rose-300"> Body sensations and emotions.</span>
                  </p>
                </div>
                <div className="bg-emerald-900/20 border border-emerald-500/30 rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <NeedIcon size={32} />
                    <h3 className="text-emerald-300 font-bold text-xl">Need</h3>
                  </div>
                  <p className="text-slate-300">
                    The universal human needs behind our feelings. 
                    <span className="text-emerald-300"> What we value and require to thrive.</span>
                  </p>
                </div>
                <div className="bg-amber-900/20 border border-amber-500/30 rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <GoldIcon size={32} />
                    <h3 className="text-amber-300 font-bold text-xl">Request</h3>
                  </div>
                  <p className="text-slate-300">
                    A clear, positive action request that could meet the need. 
                    <span className="text-amber-300"> The path to connection.</span>
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-purple-900/20 border border-purple-500/30 rounded-xl p-6 text-center">
              <p className="text-lg text-slate-200">
                This model gives us a <span className="text-purple-300 font-semibold">shared language</span> for 
                expressing ourselves authentically while maintaining connection.
              </p>
            </div>
          </div>
        );

      // CHOICE PAGE 51
      case 2:
        return <ChoiceNeuroplasticityContent />;

      // EXERCISE 1 INTRO
      case 3:
        return (
          <div className="space-y-8 animate-fadeIn" data-testid="exercise1-intro">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-gradient-to-r from-rose-500 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-rose-500/20">
                <ExerciseIcon size={40} color="#ffffff" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-4">Exercise 1: Guess the Feelings</h2>
              <p className="text-slate-300 text-lg max-w-2xl mx-auto">
                Read each sentence and, using the feelings list, make guesses as to how the speaker might be feeling.
                <span className="text-rose-300"> More than one feeling may apply.</span>
              </p>
            </div>
            
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><ObserveIcon size={20} /> Instructions</h3>
              <ul className="list-disc pl-6 space-y-2 text-slate-300">
                <li>Read each statement carefully</li>
                <li>Consider what feelings might be behind the words</li>
                <li>Use the Feelings Helper to explore options</li>
                <li>Write down multiple feelings that could apply</li>
                <li>Check your answers on the Answers page</li>
              </ul>
            </div>

            <div className="flex justify-center">
              <Button
                onClick={() => setIsHelperOpen(true)}
                className="bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-500 hover:to-pink-500 text-white px-6 py-3"
              >
                <FeelIcon size={20} />
                Open Feelings Helper
              </Button>
            </div>

            <div className="flex justify-center mt-6">
              <Button 
                onClick={() => setCurrentSection(4)}
                className="bg-white text-slate-900 hover:bg-slate-200 text-lg px-8 py-6 rounded-full"
              >
                Start Exercise <ArrowRightIcon size={24} className="ml-2" />
              </Button>
            </div>
          </div>
        );

      // EXERCISE 1 PRACTICE
      case 4:
        return (
          <div className="space-y-6 animate-fadeIn" data-testid="exercise1-practice">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Exercise 1: Guess the Feelings</h2>
              <Button
                onClick={() => setIsHelperOpen(true)}
                size="sm"
                className="bg-rose-600 hover:bg-rose-500"
              >
                                <FeelIcon size={16} className="mr-2" />
                Feelings Helper
              </Button>
            </div>

            <p className="text-slate-400 text-sm text-center">Click each statement to expand and write your guesses</p>

            <div className="space-y-3">
              {exercise1Data.map((item) => (
                <div key={item.id} className="bg-slate-800/50 border border-slate-700/50 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setExpandedItem(expandedItem === `ex1-${item.id}` ? null : `ex1-${item.id}`)}
                    className="w-full px-5 py-4 flex items-center justify-between hover:bg-slate-700/30 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <span className="bg-rose-600 text-white text-sm font-bold w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0">
                        {item.id}
                      </span>
                      <p className="text-slate-200 text-left">"{item.text}"</p>
                    </div>
                    {expandedItem === `ex1-${item.id}` ? (
                      <ChevronUp className="w-5 h-5 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    )}
                  </button>
                  
                  {expandedItem === `ex1-${item.id}` && (
                    <div className="px-5 pb-4 space-y-3 animate-in slide-in-from-top duration-200">
                      <label className="text-sm font-medium text-rose-300">What feelings might the speaker be experiencing?</label>
                      <Textarea
                        value={exercise1Guesses[item.id] || ''}
                        onChange={(e) => setExercise1Guesses(prev => ({ ...prev, [item.id]: e.target.value }))}
                        placeholder="e.g., frustrated, anxious, hopeful..."
                        className="bg-slate-900/50 border-slate-600 text-white min-h-[80px]"
                      />
                      {showAnswers[`ex1-${item.id}`] && (
                        <div className="bg-emerald-900/30 border border-emerald-500/30 rounded-lg p-3 mt-2">
                          <p className="text-emerald-300 text-sm">
                            <strong>Possible feelings:</strong> {exercise1Answers[item.id]}
                          </p>
                        </div>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowAnswers(prev => ({ ...prev, [`ex1-${item.id}`]: !prev[`ex1-${item.id}`] }))}
                        className="text-slate-400 hover:text-white"
                      >
                        {showAnswers[`ex1-${item.id}`] ? 'Hide Answer' : 'Show Answer'}
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );

      // FEELINGS WHEN NEEDS ARE MET (Page 100)
      case 5:
        return (
          <div className="space-y-8 animate-fadeIn" data-testid="feelings-satisfied">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-white mb-4">Feelings When Your Needs Are Satisfied</h2>
              <p className="text-emerald-300 text-lg">Page 100 • How we tend to feel when our needs ARE being met</p>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              {feelingsSatisfied.map((group) => (
                <div key={group.category} className="bg-emerald-900/20 border border-emerald-700/30 rounded-xl p-4">
                  <h3 className="text-emerald-400 font-bold text-sm mb-3 tracking-wider">{group.category}</h3>
                  <div className="flex flex-wrap gap-2">
                    {group.feelings.map((feeling) => (
                      <span 
                        key={feeling} 
                        className="bg-emerald-800/40 text-emerald-200 px-2 py-1 rounded text-sm"
                      >
                        {feeling}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6 text-center">
              <p className="text-slate-300">
                These feelings are signals that your needs are being <span className="text-emerald-400 font-semibold">nourished</span>.
                Notice which feelings resonate with you most often.
              </p>
              <Button
                onClick={() => navigate('/workbook/feelings-list')}
                variant="outline"
                className="mt-4 border-emerald-600 text-white hover:bg-emerald-900/20"
              >
                View Full Feelings List →
              </Button>
            </div>
          </div>
        );

      // FEELINGS WHEN NEEDS ARE NOT MET (Page 101)
      case 6:
        return (
          <div className="space-y-8 animate-fadeIn" data-testid="feelings-unsatisfied">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-white mb-4">Feelings When Your Needs Are Not Satisfied</h2>
              <p className="text-rose-300 text-lg">Page 101 • How we tend to feel when our needs are NOT being met</p>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              {feelingsUnsatisfied.map((group) => (
                <div key={group.category} className="bg-rose-900/20 border border-rose-700/30 rounded-xl p-4">
                  <h3 className="text-rose-400 font-bold text-sm mb-3 tracking-wider">{group.category}</h3>
                  <div className="flex flex-wrap gap-2">
                    {group.feelings.map((feeling) => (
                      <span 
                        key={feeling} 
                        className="bg-rose-800/40 text-rose-200 px-2 py-1 rounded text-sm"
                      >
                        {feeling}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6 text-center">
              <p className="text-slate-300">
                These feelings are signals that your needs are <span className="text-rose-400 font-semibold">unmet</span>.
                They're not "bad" - they're valuable information pointing toward your needs.
              </p>
            </div>
          </div>
        );

      // EXERCISE 2 INTRO
      case 7:
        return (
          <div className="space-y-8 animate-fadeIn" data-testid="exercise2-intro">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-blue-500/20">
                <ThoughtIcon size={40} color="#ffffff" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-4">Exercise 2: Feeling or Thought?</h2>
              <p className="text-slate-300 text-lg max-w-2xl mx-auto">
                Determine whether each statement expresses a <span className="text-emerald-400 font-semibold">genuine feeling</span> or a 
                <span className="text-amber-400 font-semibold"> thought/evaluation</span>. Translate thoughts into actual feelings.
              </p>
            </div>
            
            <div className="bg-blue-900/20 border border-blue-700/30 rounded-xl p-6">
              <h3 className="text-blue-300 font-bold mb-4">💡 Example</h3>
              <div className="space-y-3">
                <p className="text-slate-300">
                  <span className="text-amber-400">"I'm feeling that I'm not ready to have children yet."</span>
                </p>
                <p className="text-slate-400 text-sm">This is a <span className="text-amber-400">THOUGHT</span>, not a feeling.</p>
                <p className="text-slate-300 mt-2">
                  <span className="text-emerald-400">Translation: "When I think about becoming a parent, I am afraid."</span>
                </p>
              </div>
            </div>

            <div className="flex justify-center mt-6">
              <Button 
                onClick={() => setCurrentSection(8)}
                className="bg-white text-slate-900 hover:bg-slate-200 text-lg px-8 py-6 rounded-full"
              >
                Start Exercise <ArrowRightIcon size={24} className="ml-2" />
              </Button>
            </div>
          </div>
        );

      // EXERCISE 2 PRACTICE
      case 8:
        return (
          <div className="space-y-6 animate-fadeIn" data-testid="exercise2-practice">
            <h2 className="text-2xl font-bold text-white mb-6">Exercise 2: Feeling or Thought?</h2>

            <div className="space-y-4">
              {exercise2Data.map((item) => {
                const userSelection = exercise2Selections[item.id];
                const isRevealed = showExercise2Answers;
                const isCorrect = userSelection === item.isFeeling;

                return (
                  <div key={item.id} className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
                    <div className="flex items-start gap-3 mb-4">
                      <span className="bg-blue-600 text-white text-sm font-bold w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        {item.id}
                      </span>
                      <p className="text-slate-200 text-lg">"{item.text}"</p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <button
                        onClick={() => setExercise2Selections(prev => ({ ...prev, [item.id]: true }))}
                        className={`p-3 rounded-lg border-2 transition-all ${
                          userSelection === true 
                            ? 'bg-emerald-600/30 border-emerald-500 text-emerald-300' 
                            : 'bg-slate-700/30 border-slate-600 hover:border-emerald-500/50 text-slate-400'
                        }`}
                      >
                        <CheckIcon size={20} color="#10B981" />
                        <span className="text-sm">Genuine Feeling</span>
                      </button>
                      <button
                        onClick={() => setExercise2Selections(prev => ({ ...prev, [item.id]: false }))}
                        className={`p-3 rounded-lg border-2 transition-all ${
                          userSelection === false 
                            ? 'bg-amber-600/30 border-amber-500 text-amber-300' 
                            : 'bg-slate-700/30 border-slate-600 hover:border-amber-500/50 text-slate-400'
                        }`}
                      >
                        <X className="w-5 h-5 mx-auto mb-1" />
                        <span className="text-sm">Thought/Evaluation</span>
                      </button>
                    </div>

                    {isRevealed && userSelection !== undefined && (
                      <div className={`p-3 rounded-lg ${isCorrect ? 'bg-emerald-900/30 border border-emerald-500/30' : 'bg-rose-900/30 border border-rose-500/30'}`}>
                        <p className={`text-sm font-medium ${isCorrect ? 'text-emerald-300' : 'text-rose-300'}`}>
                          {isCorrect ? '✓ Correct!' : '✗ Not quite...'} This is a {item.isFeeling ? 'genuine feeling' : 'thought/evaluation'}.
                        </p>
                        {!item.isFeeling && item.translation && (
                          <p className="text-slate-300 text-sm mt-2">
                            <strong className="text-blue-300">Translation:</strong> {item.translation}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Notes Section */}
                    <div className="mt-4 pt-4 border-t border-slate-700/50">
                      <label className="text-sm font-medium text-purple-300 mb-2 block flex items-center gap-2">
                        <ThoughtIcon size={16} color="#A855F7" />
                        My Notes & Reflections
                      </label>
                      <Textarea
                        value={exercise2Notes[item.id] || ''}
                        onChange={(e) => setExercise2Notes(prev => ({ ...prev, [item.id]: e.target.value }))}
                        placeholder="Write your thoughts about this statement... What feelings might be underneath? What needs might be unmet?"
                        className="bg-purple-900/20 border-purple-700/30 text-white min-h-[80px]"
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex justify-center mt-6">
              <Button
                onClick={() => setShowExercise2Answers(!showExercise2Answers)}
                variant="outline"
                className="border-blue-600 text-white hover:bg-blue-900/20"
              >
                {showExercise2Answers ? 'Hide Answers' : 'Check All Answers'}
              </Button>
            </div>
          </div>
        );

      // EXERCISE 3
      case 9:
        return (
          <div className="space-y-8 animate-fadeIn" data-testid="exercise3">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-white mb-4">Exercise 3: Observations & Feelings</h2>
              <p className="text-slate-300 text-lg">
                Write down at least five things you observed this week and the feelings that were stimulated in you.
              </p>
            </div>

            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((num) => (
                <div key={num} className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="bg-purple-600 text-white font-bold w-8 h-8 rounded-full flex items-center justify-center">
                      {num}
                    </span>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-blue-300 mb-2 block">When...</label>
                      <Textarea
                        value={exercise3Responses[num]?.when || ''}
                        onChange={(e) => setExercise3Responses(prev => ({
                          ...prev,
                          [num]: { ...prev[num], when: e.target.value }
                        }))}
                        placeholder="Describe what you observed..."
                        className="bg-blue-900/20 border-blue-700/30 text-white min-h-[80px]"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-rose-300 mb-2 block">I felt...</label>
                      <Textarea
                        value={exercise3Responses[num]?.felt || ''}
                        onChange={(e) => setExercise3Responses(prev => ({
                          ...prev,
                          [num]: { ...prev[num], felt: e.target.value }
                        }))}
                        placeholder="Name the feelings..."
                        className="bg-rose-900/20 border-rose-700/30 text-white min-h-[80px]"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      // NON-FEELING WORDS (Page 103)
      case 10:
        return (
          <div className="space-y-8 animate-fadeIn" data-testid="non-feeling-words">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-white mb-4">Non-Feeling Words</h2>
              <p className="text-amber-300 text-lg">Page 103 • Words that describe what we think others are doing to us</p>
            </div>

            <div className="bg-amber-900/20 border border-amber-700/30 rounded-xl p-6">
              <p className="text-slate-300 mb-6">
                These words <span className="text-amber-400 font-semibold">look like feelings</span> but actually describe 
                what we think someone else is doing <em>to us</em>. They contain implied blame and don't connect us to our actual feelings and needs.
              </p>
              
              <div className="flex flex-wrap gap-2">
                {nonFeelingWords.map((word) => (
                  <span 
                    key={word} 
                    className="bg-amber-800/40 text-amber-200 px-3 py-1.5 rounded-lg text-sm hover:bg-amber-700/50 transition-colors cursor-default"
                  >
                    {word}
                  </span>
                ))}
              </div>
            </div>

            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
              <h3 className="text-white font-bold mb-4">🔄 Translation Pattern</h3>
              <div className="space-y-4">
                <div className="bg-amber-900/20 rounded-lg p-4">
                  <p className="text-amber-300"><strong>Non-feeling:</strong> "I feel rejected"</p>
                </div>
                <ArrowRightIcon size={24} color="#64748B" />
                <div className="bg-emerald-900/20 rounded-lg p-4">
                  <p className="text-emerald-300"><strong>Feeling + Need:</strong> "I feel hurt because I need belonging"</p>
                </div>
              </div>
            </div>
          </div>
        );

      // EXERCISE 4
      case 11:
        return (
          <div className="space-y-8 animate-fadeIn" data-testid="exercise4">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-white mb-4">Exercise 4: Translate Non-Feeling Words</h2>
              <p className="text-slate-300 text-lg max-w-2xl mx-auto">
                Think of four scenarios where you might use non-feeling words and translate them into genuine feelings and needs.
              </p>
            </div>

            <div className="bg-blue-900/20 border border-blue-700/30 rounded-xl p-5 mb-6">
              <h3 className="text-blue-300 font-bold mb-3">💡 Example</h3>
              <div className="space-y-2 text-sm text-slate-300">
                <p><strong className="text-amber-300">Non-feeling word:</strong> Rejected</p>
                <p><strong className="text-slate-400">Scenario:</strong> When she told me she didn't like me, I felt rejected.</p>
                <p><strong className="text-emerald-300">Translation:</strong> When she told me she didn't like me, I felt hurt because I had a need for companionship.</p>
              </div>
            </div>

            <div className="space-y-6">
              {[1, 2, 3, 4].map((num) => (
                <div key={num} className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="bg-amber-600 text-white font-bold w-8 h-8 rounded-full flex items-center justify-center">
                      {num}
                    </span>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-amber-300 mb-2 block">Non-feeling word:</label>
                      <input
                        type="text"
                        value={exercise4Responses[num]?.word || ''}
                        onChange={(e) => setExercise4Responses(prev => ({
                          ...prev,
                          [num]: { ...prev[num], word: e.target.value }
                        }))}
                        placeholder="e.g., abandoned, betrayed, ignored..."
                        className="w-full bg-amber-900/20 border border-amber-700/30 text-white px-4 py-2 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-300 mb-2 block">Scenario with non-feeling word:</label>
                      <Textarea
                        value={exercise4Responses[num]?.scenario || ''}
                        onChange={(e) => setExercise4Responses(prev => ({
                          ...prev,
                          [num]: { ...prev[num], scenario: e.target.value }
                        }))}
                        placeholder="When [observation], I felt [non-feeling word]..."
                        className="bg-slate-900/50 border-slate-600 text-white min-h-[60px]"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-emerald-300 mb-2 block">Translation (feeling + need):</label>
                      <Textarea
                        value={exercise4Responses[num]?.translation || ''}
                        onChange={(e) => setExercise4Responses(prev => ({
                          ...prev,
                          [num]: { ...prev[num], translation: e.target.value }
                        }))}
                        placeholder="When [observation], I felt [genuine feeling] because I need [need]..."
                        className="bg-emerald-900/20 border-emerald-700/30 text-white min-h-[60px]"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      // EXERCISE 1 ANSWERS (Page 104)
      case 12:
        return (
          <div className="space-y-8 animate-fadeIn" data-testid="exercise1-answers">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-white mb-4">Exercise 1: Possible Answers</h2>
              <p className="text-emerald-300 text-lg">Page 104 • Compare your guesses with these suggestions</p>
            </div>

            <div className="grid gap-4">
              {exercise1Data.map((item) => (
                <div 
                  key={item.id} 
                  className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 hover:border-emerald-500/30 transition-colors cursor-pointer"
                  onClick={() => setShowAnswers(prev => ({ ...prev, [`answer-${item.id}`]: !prev[`answer-${item.id}`] }))}
                >
                  <div className="flex items-start gap-3">
                    <span className="bg-rose-600 text-white text-sm font-bold w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      {item.id}
                    </span>
                    <div className="flex-1">
                      <p className="text-slate-300 mb-2">"{item.text}"</p>
                      {showAnswers[`answer-${item.id}`] !== false && (
                        <div className="bg-emerald-900/20 border border-emerald-500/20 rounded-lg p-3 animate-in fade-in">
                          <p className="text-emerald-300 text-sm">
                            <strong>Possible feelings:</strong> {exercise1Answers[item.id]}
                          </p>
                        </div>
                      )}
                    </div>
                    <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${showAnswers[`answer-${item.id}`] === false ? '' : 'rotate-180'}`} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      // EXERCISE 2 ANSWERS (Page 104)
      case 13:
        return (
          <div className="space-y-8 animate-fadeIn" data-testid="exercise2-answers">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-white mb-4">Exercise 2: Possible Answers</h2>
              <p className="text-blue-300 text-lg">Page 104 • Feeling vs Thought translations</p>
            </div>

            <div className="space-y-4">
              {exercise2Data.map((item) => (
                <div key={item.id} className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
                  <div className="flex items-start gap-3 mb-3">
                    <span className="bg-blue-600 text-white text-sm font-bold w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      {item.id}
                    </span>
                    <p className="text-slate-200">"{item.text}"</p>
                  </div>
                  
                  <div className={`p-3 rounded-lg ${item.isFeeling ? 'bg-emerald-900/20 border border-emerald-500/20' : 'bg-amber-900/20 border border-amber-500/20'}`}>
                    <p className={`text-sm font-medium ${item.isFeeling ? 'text-emerald-300' : 'text-amber-300'}`}>
                      This is a {item.isFeeling ? 'FEELING' : 'THOUGHT'}
                    </p>
                    {!item.isFeeling && item.translation && (
                      <p className="text-slate-300 text-sm mt-2">
                        <strong className="text-blue-300">Translation:</strong> {item.translation}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      // ALARM FEELINGS (Page 105)
      case 14:
        return (
          <div className="space-y-8 animate-fadeIn" data-testid="alarm-feelings">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-gradient-to-r from-amber-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-amber-500/20">
                <AlarmIcon size={40} />
              </div>
              <h2 className="text-3xl font-bold text-white mb-2">Alarm Feelings</h2>
              <p className="text-amber-300 text-lg">Anger, Guilt, Shame & Depression</p>
              <p className="text-slate-400 text-sm mt-2">(also known as Gateway Feelings)</p>
              <p className="text-slate-500 text-xs mt-1">From LaShelle Charde's Connection Gem of the Week</p>
            </div>

            <div className="bg-amber-900/20 border border-amber-700/30 rounded-xl p-6">
              <p className="text-slate-200 leading-relaxed">
                Anger, guilt, shame, & depression are <span className="text-amber-400 font-semibold">important alarms</span>. They let you know three things:
              </p>
              <ol className="list-decimal pl-6 mt-4 space-y-2 text-slate-300">
                <li>You are <span className="text-rose-400">disconnected from your needs</span></li>
                <li>Unmet needs are up</li>
                <li>Critical voices have been running rampant either consciously or subconsciously</li>
              </ol>
            </div>

            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
              <p className="text-slate-300 leading-relaxed">
                These alarm feelings aren't "bad". It's important to identify them and feel them. It's equally important 
                <span className="text-amber-400"> not to take action or make decisions based on them</span> because their 
                very nature is reactive. If you take action from one of these feelings, your view will be narrow and 
                likely distorted. You will end up creating more suffering.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Anger */}
              <div className="bg-red-900/20 border border-red-700/30 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-3">
                  <AngerIcon size={24} />
                  <h3 className="text-xl font-bold text-red-300">Anger</h3>
                </div>
                <p className="text-slate-300 text-sm leading-relaxed">
                  Behind anger, the most basic thought is that <span className="text-red-300">things should be different</span> than 
                  they are. The word "should" can lead you quickly to a disconnected state. Anger can also indicate progress—you're 
                  beginning to honor yourself and recognize that your needs were not met.
                </p>
              </div>

              {/* Guilt & Shame */}
              <div className="bg-purple-900/20 border border-purple-700/30 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-3">
                  <GuiltShameIcon size={24} />
                  <h3 className="text-xl font-bold text-purple-300">Guilt & Shame</h3>
                </div>
                <p className="text-slate-300 text-sm leading-relaxed">
                  The voice of guilt says <span className="text-purple-300">"I shouldn't have done that, it was bad."</span> 
                  The voice of shame says <span className="text-purple-300">"I shouldn't have done that, I am bad."</span> 
                  Guilt and shame are only useful when they lead you to connect with unmet needs.
                </p>
              </div>

              {/* Depression */}
              <div className="bg-slate-700/50 border border-slate-600/30 rounded-xl p-5 md:col-span-2">
                <div className="flex items-center gap-3 mb-3">
                  <SadnessIcon size={24} />
                  <h3 className="text-xl font-bold text-slate-300">Depression</h3>
                </div>
                <p className="text-slate-300 text-sm leading-relaxed">
                  The voice of depression <span className="text-slate-200">denies your existence</span> in some way. Specific 
                  phrases might include: "I shouldn't feel sad", "I shouldn't have those needs", "I shouldn't take up space 
                  or ask for what I want."
                </p>
              </div>
            </div>
          </div>
        );

      // ACCEPTANCE, CLARITY, MOURNING (Page 106)
      case 15:
        return (
          <div className="space-y-8 animate-fadeIn" data-testid="acceptance-mourning">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-white mb-4">Acceptance, Clarity, and Mourning</h2>
              <p className="text-teal-300 text-lg">Page 106 • How to work with Alarm Feelings</p>
            </div>

            <div className="space-y-6">
              {/* First */}
              <div className="bg-teal-900/20 border border-teal-700/30 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <span className="bg-teal-600 text-white font-bold w-8 h-8 rounded-full flex items-center justify-center">1</span>
                  <h3 className="text-xl font-bold text-teal-300">Accept</h3>
                </div>
                <p className="text-slate-300 leading-relaxed">
                  Accept the way things are. Not submit or accept in a hopeless way, but rather 
                  <span className="text-teal-300"> acknowledge what is or what was without resistance</span>. 
                  Let yourself feel. Resistance can take many forms—tensing the body, telling stories to excuse 
                  or justify what happened, or engaging the voices of anger, guilt, shame or depression.
                </p>
              </div>

              {/* Second */}
              <div className="bg-blue-900/20 border border-blue-700/30 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <span className="bg-blue-600 text-white font-bold w-8 h-8 rounded-full flex items-center justify-center">2</span>
                  <h3 className="text-xl font-bold text-blue-300">Recognize</h3>
                </div>
                <p className="text-slate-300 leading-relaxed">
                  Recognize that anger, guilt, shame, & depression don't arise because of another's words or behavior. 
                  They can only arise if you are having the thoughts and/or beliefs mentioned above. 
                  <span className="text-blue-300"> Listen for those thoughts in your consciousness</span>. What are the exact words? 
                  Writing them down can help.
                </p>
              </div>

              {/* Third */}
              <div className="bg-purple-900/20 border border-purple-700/30 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <span className="bg-purple-600 text-white font-bold w-8 h-8 rounded-full flex items-center justify-center">3</span>
                  <h3 className="text-xl font-bold text-purple-300">Feel Deeper</h3>
                </div>
                <p className="text-slate-300 leading-relaxed">
                  Feel the feelings <span className="text-purple-300">below</span> anger, guilt, shame, & depression. 
                  Sadness or regret for needs unmet is usually underneath. Feeling sadness or regret requires a level 
                  of vulnerability and responsibility that isn't present with alarm feelings. From this place of connection 
                  to more vulnerable feelings, you can move to the needs unmet.
                </p>
              </div>
            </div>

            {/* 5-Step Practice */}
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700 rounded-xl p-6">
              <h3 className="text-white font-bold text-lg mb-4 text-center">📝 When You Notice an Alarm Feeling</h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3 bg-slate-800/50 p-3 rounded-lg">
                  <span className="bg-slate-600 text-white text-sm font-bold w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0">1</span>
                  <p className="text-slate-300">Name what happened in <span className="text-blue-300">neutral observational terms</span></p>
                </div>
                <div className="flex items-start gap-3 bg-slate-800/50 p-3 rounded-lg">
                  <span className="bg-slate-600 text-white text-sm font-bold w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0">2</span>
                  <p className="text-slate-300">Identify what you are <span className="text-amber-300">telling yourself</span> about what happened</p>
                </div>
                <div className="flex items-start gap-3 bg-slate-800/50 p-3 rounded-lg">
                  <span className="bg-slate-600 text-white text-sm font-bold w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0">3</span>
                  <p className="text-slate-300">Connect with the <span className="text-rose-300">feelings and needs</span> underneath the alarm</p>
                </div>
                <div className="flex items-start gap-3 bg-slate-800/50 p-3 rounded-lg">
                  <span className="bg-slate-600 text-white text-sm font-bold w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0">4</span>
                  <p className="text-slate-300">Let yourself <span className="text-purple-300">mourn</span> the needs that went or are still unmet</p>
                </div>
                <div className="flex items-start gap-3 bg-slate-800/50 p-3 rounded-lg">
                  <span className="bg-slate-600 text-white text-sm font-bold w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0">5</span>
                  <p className="text-slate-300">Decide what <span className="text-emerald-300">action</span> you would like to take to meet your needs</p>
                </div>
              </div>
              <p className="text-slate-500 text-xs text-center mt-4">From LaShelle Charde's Connection Gem of the Week • www.wiseheartpdx.org</p>
            </div>
          </div>
        );

      // HOMEWORK (Page 107)
      case 16:
        return (
          <div className="space-y-8 animate-fadeIn" data-testid="chapter3-homework">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-indigo-500/20">
                <HomeworkIcon size={40} color="#ffffff" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-4">Chapter 3 Homework</h2>
              <p className="text-slate-300 text-lg">Page 107 • Practice for the week ahead</p>
            </div>

            {/* Reading */}
            <div className="bg-blue-900/20 border border-blue-700/30 rounded-xl p-6">
              <h3 className="text-blue-300 font-bold text-lg mb-4 flex items-center gap-2">
                <ReadingIcon size={20} /> Reading
              </h3>
              <ul className="space-y-3 text-slate-300">
                <li className="flex items-start gap-2">
                  <CheckIcon size={20} color="#3B82F6" />
                  <span>Read page 1 in this notebook: "Alarm Feelings: Anger, Guilt, Shame, and Depression"</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckIcon size={20} color="#3B82F6" />
                  <span>Read chapter 5 in <em>Nonviolent Communication</em>: "Taking Responsibility for Our Feelings"</span>
                </li>
              </ul>
            </div>

            {/* Video */}
            <div className="bg-purple-900/20 border border-purple-700/30 rounded-xl p-6">
              <h3 className="text-purple-300 font-bold text-lg mb-4 flex items-center gap-2">
                <VideoIcon size={20} /> Watch Video
              </h3>
              <p className="text-slate-300">
                Part 2 of the NVC basics video: <span className="text-purple-300">"Expressing Needs and Requests"</span>
                <br />
                <span className="text-slate-400 text-sm">Time index: 47:18 - 1:30:38</span>
              </p>
            </div>

            {/* Partner Exercises */}
            <div className="bg-emerald-900/20 border border-emerald-700/30 rounded-xl p-6">
              <h3 className="text-emerald-300 font-bold text-lg mb-4 flex items-center gap-2">
                <PartnerIcon size={20} /> Partner Exercises
              </h3>
              <p className="text-slate-400 text-sm mb-4 italic">
                (If you don't know your partner well, spend a few minutes getting to know each other first)
              </p>

              <div className="space-y-6">
                {/* Exercise 1 */}
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <h4 className="text-white font-bold mb-2">1. Express the Feelings Exercise</h4>
                  <p className="text-slate-300 text-sm mb-3">
                    Come up with three things people said or did that upset you. Tell your partner:
                  </p>
                  <ul className="list-disc pl-5 space-y-1 text-slate-300 text-sm">
                    <li>The pure observation</li>
                    <li>The feelings stimulated</li>
                    <li>The need</li>
                  </ul>
                  <div className="bg-emerald-900/30 rounded-lg p-3 mt-3">
                    <p className="text-emerald-200 text-sm">
                      <strong>Use this form:</strong> When _____, I felt _____ because I need _____.
                    </p>
                  </div>
                  <p className="text-slate-400 text-xs mt-2">
                    Partner listens and coaches for accuracy (e.g., if they hear a thought being expressed as a feeling).
                  </p>
                </div>

                {/* Exercise 2 */}
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <h4 className="text-white font-bold mb-2">2. Hear the Feelings Exercise</h4>
                  <p className="text-slate-300 text-sm mb-3">
                    <strong>Partner A talks</strong> about high/low points (don't try to talk in NVC, just tell a story):
                  </p>
                  <ul className="list-disc pl-5 space-y-1 text-slate-300 text-sm">
                    <li>The last seven days</li>
                    <li>Your current relationship</li>
                    <li>Your best friend</li>
                    <li>Dealing with your parents</li>
                    <li>Your relationship with your children</li>
                  </ul>
                  <p className="text-slate-300 text-sm mt-3">
                    <strong>Partner B stops Partner A</strong> at about 1.5 minutes and asks:
                    <span className="text-emerald-300"> "What are you feeling and needing?"</span>
                  </p>
                  <p className="text-slate-400 text-xs mt-2">
                    Partner A speaks for 8 minutes total. Partner B helps identify feelings. Then switch roles.
                  </p>
                </div>

                {/* Personal Reflections */}
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <h4 className="text-white font-bold mb-2">3. Personal Reflections for the Week</h4>
                  <p className="text-slate-300 text-sm">
                    Throughout the week, whenever you have a strong positive or negative judgment about something:
                  </p>
                  <ul className="list-disc pl-5 space-y-1 text-slate-300 text-sm mt-2">
                    <li>Try to <span className="text-rose-300">identify your feelings</span></li>
                    <li>Notice feelings related to what your friend, parent, classmate, or partner said or did</li>
                    <li>In your mind, make an observation and then identify the feeling(s)</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-center gap-4 mt-8">
              <Button
                onClick={() => navigate('/workbook/chapter-2')}
                variant="outline"
                className="border-slate-600 text-white hover:bg-slate-800"
              >
                <ArrowLeftIcon size={16} className="mr-2" /> Chapter 2
              </Button>
              <Button
                onClick={() => setCurrentSection(17)}
                className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-slate-900 font-semibold"
              >
                <FileText className="w-4 h-4 mr-2" />
                Create My Summary
              </Button>
              <Button
                onClick={() => navigate('/belonging-ebook')}
                className="bg-white text-slate-900 hover:bg-slate-200"
              >
                Return to E-Book <ArrowRightIcon size={16} className="ml-2" />
              </Button>
            </div>
          </div>
        );

      // CHAPTER SUMMARY
      case 17:
        return (
          <div className="space-y-8 animate-fadeIn" data-testid="chapter-summary">
            <div className="text-center mb-8">
              <div className="w-24 h-24 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-2xl shadow-amber-500/30">
                <GoldIcon size={48} />
              </div>
              <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-yellow-200 mb-4">
                Create Your Chapter 3 Summary
              </h2>
              <p className="text-slate-300 text-lg">
                Congratulations on completing Chapter 3: Feelings!
              </p>
            </div>

            <div className="bg-gradient-to-br from-amber-900/30 to-yellow-900/20 border border-amber-500/30 rounded-2xl p-8 text-center">
              <h3 className="text-xl font-bold text-amber-300 mb-4">Your Journey Summary</h3>
              <p className="text-slate-300 mb-6">
                Generate a beautiful PDF document containing all your exercise responses, 
                reflections, and insights from this chapter. Perfect for review or sharing with your practice partner.
              </p>
              
              <div className="grid md:grid-cols-2 gap-4 mb-6 text-left">
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <h4 className="text-emerald-400 font-medium mb-2 flex items-center gap-2">
                    <CheckIcon size={16} color="#10B981" />
                    What's Included
                  </h4>
                  <ul className="text-sm text-slate-300 space-y-1">
                    <li>• Exercise 1: Feelings guesses</li>
                    <li>• Exercise 2: Feeling vs Thought analysis</li>
                    <li>• Exercise 3: Observations journal</li>
                    <li>• Exercise 4: Non-feeling translations</li>
                  </ul>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <h4 className="text-purple-400 font-medium mb-2 flex items-center gap-2">
                    <InsightIcon size={16} color="#A855F7" />
                    Key Learnings
                  </h4>
                  <ul className="text-sm text-slate-300 space-y-1">
                    <li>• Genuine feelings vs thoughts</li>
                    <li>• Non-feeling words awareness</li>
                    <li>• Alarm feelings understanding</li>
                    <li>• Self-directed neuroplasticity</li>
                  </ul>
                </div>
              </div>

              <Button
                onClick={() => setShowSummaryGenerator(true)}
                className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-slate-900 font-bold text-lg px-8 py-6 rounded-xl shadow-lg shadow-amber-500/30"
              >
                <FileText className="w-5 h-5 mr-2" />
                Generate My PDF Summary
              </Button>
            </div>

            {/* Navigation */}
            <div className="flex justify-center gap-4 mt-8">
              <Button
                onClick={() => setCurrentSection(16)}
                variant="outline"
                className="border-slate-600 text-white hover:bg-slate-800"
              >
                <ArrowLeftIcon size={16} className="mr-2" /> Back to Homework
              </Button>
              <Button
                onClick={() => navigate('/belonging-ebook')}
                className="bg-white text-slate-900 hover:bg-slate-200"
              >
                Return to E-Book <ArrowRightIcon size={16} className="ml-2" />
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={isPresentationMode ? "fixed inset-0 z-50 bg-slate-900 overflow-auto" : "min-h-screen bg-slate-900"} data-testid="chapter3-presentation">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-slate-900/90 backdrop-blur-md border-b border-slate-800">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {!isPresentationMode && (
              <Button variant="ghost" size="sm" onClick={() => navigate('/belonging-ebook')} className="text-slate-400 hover:text-white">
                <ArrowLeftIcon size={16} className="mr-2" /> Back
              </Button>
            )}
            <h1 className="font-bold text-white hidden md:block">Chapter 3: Feelings</h1>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Sync Status */}
            {isLoggedIn && (
              <div className="flex items-center gap-1 text-xs text-slate-400 mr-2">
                {isSyncing ? (
                  <><Cloud className="h-3 w-3 animate-pulse text-blue-400" /><span>Syncing...</span></>
                ) : lastSyncTime ? (
                  <><Cloud className="h-3 w-3 text-green-400" /><span>Synced</span></>
                ) : (
                  <><CloudOff className="h-3 w-3 text-slate-500" /><span>Not synced</span></>
                )}
              </div>
            )}
            
            <span className="text-xs text-slate-500 mr-2">
              {currentSection + 1} / {sections.length}
            </span>
            
            <Button 
              variant="outline" 
              size="sm" 
              onClick={saveProgress}
              disabled={isSyncing}
              className="border-slate-700 text-slate-300 hover:text-white"
            >
                            <SaveIcon size={16} />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={resetAll}
              className="border-red-700 text-red-400 hover:text-red-300 hover:bg-red-900/20"
            >
              <RotateCcw className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={togglePresentationMode}
              className="text-purple-400 hover:text-purple-300 hover:bg-purple-900/20"
            >
              {isPresentationMode ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </Button>
          </div>
        </div>
        <Progress value={progress_pct} className="h-1 bg-slate-800" />
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8 lg:py-12 min-h-[calc(100vh-140px)]">
        <div className="max-w-4xl mx-auto">
          {renderContent()}
        </div>
      </div>

      {/* Footer Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-slate-900/90 backdrop-blur border-t border-slate-800 p-4 z-40">
        <div className="container mx-auto flex items-center justify-between">
          <Button
            onClick={() => setCurrentSection(prev => Math.max(0, prev - 1))}
            disabled={currentSection === 0}
            variant="ghost"
            className="text-slate-400 hover:text-white"
          >
            <ArrowLeftIcon size={24} className="mr-2" /> Previous
          </Button>

          <div className="hidden md:block text-slate-500 text-sm font-medium">
            {sections[currentSection].title} {sections[currentSection].page && `• Page ${sections[currentSection].page}`}
          </div>

          <Button
            onClick={() => setCurrentSection(prev => Math.min(sections.length - 1, prev + 1))}
            disabled={currentSection === sections.length - 1}
            className="bg-white text-slate-900 hover:bg-slate-200"
          >
            Next <ArrowRightIcon size={24} className="ml-2" />
          </Button>
        </div>
      </div>

      {/* Feelings & Needs Helper Modal */}
      {isHelperOpen && (
        <FeelingsNeedsHelper onClose={() => setIsHelperOpen(false)} />
      )}

      {/* Floating Feelings Helper - Always Available */}
      <FloatingFeelingsHelper />

      {/* Chapter Summary Generator Modal */}
      {showSummaryGenerator && (
        <ChapterSummaryGenerator
          chapterNumber={3}
          chapterTitle="Feelings"
          responses={{
            exercise1: exercise1Guesses,
            exercise2Notes: exercise2Notes,
            exercise3: exercise3Responses,
            exercise4: exercise4Responses,
          }}
          exercises={[
            { id: 'exercise1', title: 'Exercise 1: Guess the Feelings', page: 99 },
            { id: 'exercise2Notes', title: 'Exercise 2: Feeling or Thought?', page: 102 },
            { id: 'exercise3', title: 'Exercise 3: Observations & Feelings', page: 102 },
            { id: 'exercise4', title: 'Exercise 4: Non-Feeling Words', page: 103 },
          ]}
          onClose={() => setShowSummaryGenerator(false)}
        />
      )}
    </div>
  );
}
